import { Alert, Box, Button, ButtonBase, Stack, Typography } from '@mui/material';
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../authentication/useAuth';
import useIdleTimeout from '../../hooks/useIdleTimeout';
import PasswordPrompt from '../../PasswordPrompt';
import { setSelectedSite } from '../../store/global'
import { tokens } from '../../theme';
import { getAllItems } from '../Administration/InventoryItems/slice';
import { getAllSites } from '../Administration/Sites/slice';
import { getAllUsers } from '../Administration/Users/slice';
import { getAllVehicles } from '../Administration/Vehicles/slice';
import { getAllGRVs } from '../Operations/GRVs/slice';
import { getAllActiveJobCards } from '../Operations/JobCards/slice';
import AuthShell from './AuthShell';

const SiteSelection = () => {
    const [error, setError] = useState("");
    const { companyId, siteAccess, logout, user } = useAuth();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // siteAccess holds only the sites this user may open; choosing one goes straight in
    const handleSelectSite = (site) => {
        if (siteAccess.map(site => site._id).includes(site._id)) {
            dispatch(setSelectedSite(site));
            dispatch(getAllUsers(companyId));
            dispatch(getAllSites(companyId));
            dispatch(getAllItems(companyId, site.siteId));
            dispatch(getAllVehicles(companyId, site.siteId));
            dispatch(getAllActiveJobCards(companyId, site.siteId));
            dispatch(getAllGRVs(companyId, site.siteId));
            localStorage.setItem('selectedSite', JSON.stringify(site));
            navigate('/ims/operations/job-cards', { replace: true })
        }
        else {
            setError("You do not have access to this site");
        }
    }

    const [openModal, setOpenModal] = React.useState(false);

    // Why the password box is open: 'idle' (15 minutes without activity) or 'limit' (the 12 hours are up)
    const handleIdle = (reason) => {
        setOpenModal(reason);
    }

    const { idleTimer } = useIdleTimeout({ onIdle: handleIdle });

    const stay = () => {
        setOpenModal(false);
        idleTimer.reset();
    }

    const handleLogout = () => {
        logout();
        setOpenModal(false);
    }

    const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ');

    return (
        <AuthShell>
            <PasswordPrompt open={!!openModal} reason={openModal} stay={stay} handleLogout={handleLogout} />
            <Stack spacing={3}>
                <div>
                    <Typography component="h2" sx={{ fontSize: 32, fontWeight: 600, lineHeight: 1.2 }}>Choose a site</Typography>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                        <Typography color="text.secondary">{name ? `Signed in as ${name}.` : 'Signed in.'}</Typography>
                        <Button size="small" onClick={logout} sx={{ minWidth: 0, px: 0.5 }}>Sign out</Button>
                    </Stack>
                </div>
                {siteAccess?.length ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                        {siteAccess.map((site) => (
                            <ButtonBase
                                key={site.siteId}
                                onClick={() => handleSelectSite(site)}
                                aria-label={`Open ${site.siteName}`}
                                sx={{
                                    display: 'block', textAlign: 'left', p: 2, borderRadius: 2,
                                    bgcolor: tokens.surface, border: `1px solid ${tokens.border}`,
                                    '&:hover': { borderColor: tokens.brand, bgcolor: tokens.surfaceHover },
                                    '&.Mui-focusVisible': { outline: `3px solid ${tokens.accent}`, outlineOffset: 2 },
                                }}
                            >
                                <Typography sx={{ fontSize: 24, fontWeight: 700, color: tokens.brand, lineHeight: 1.1 }}>{site.siteCode || site.siteName.slice(0, 3).toUpperCase()}</Typography>
                                <Typography sx={{ mt: 0.5, fontWeight: 500, color: tokens.ink }} noWrap>{site.siteName}</Typography>
                            </ButtonBase>
                        ))}
                    </Box>
                ) : (
                    <Alert severity="info">Your account has no sites yet. Ask an administrator to give you access to one.</Alert>
                )}
                {error && <Alert severity="error">{error}</Alert>}
            </Stack>
        </AuthShell>
    )
}

export default SiteSelection
